import { useRef, type ReactNode } from 'react';
import { SEAT_NAMES, TRICKS_TO_SAVE, teamOfSeat, type Seat } from '../game/match';
import type { SeatPlayer, Teams } from '../shared/schemas';
import { DealArrow } from './DealArrow';
import { Avatar, Name, teamAvatarFill } from './ui';

interface HandProgress {
  dealInTurn: number;
  dealsPerTurn: number;
  lastOfTurn: boolean;
  nextDealerName: string | null;
  pips: ReactNode;
}

interface Props {
  seats: SeatPlayer[];
  teams: Teams;
  dealer?: Seat | null;
  caller?: Seat | null;
  dealt?: Record<Seat, number>;
  dealsPerPlayer?: number;
  /** Guaa from deals in which this player chose Rang. */
  guaa?: Record<Seat, number>;
  center?: ReactNode;
  /** This turn's hand progress, shown in a header band above the felt. Omit for a bare table. */
  hand?: HandProgress;
  /** The caller's team name, when Rang goes to a specific team. */
  callerTeamName?: string | null;
  /** Repeats the spoken "who's dealing" announcement; hidden when the device can't speak. */
  onSpeak?: () => void;
}

// Grid placement for a table seen from above.
const AREA: Record<Seat, string> = {
  0: 'col-start-2 row-start-1',
  1: 'col-start-3 row-start-2',
  2: 'col-start-2 row-start-3',
  3: 'col-start-1 row-start-2',
};

export function TableView({ seats, teams, dealer, caller, dealt, dealsPerPlayer, guaa, center, hand, callerTeamName, onSpeak }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  // The arrow only makes sense while someone is actively dealing to someone else.
  const dealing = dealer !== null && caller !== null && dealer !== undefined && caller !== undefined && dealer !== caller;
  const callerTeamColor = dealing ? teams[teamOfSeat(caller)].color : undefined;
  const dealerName = dealer != null ? seats[dealer]!.name : null;

  return (
    <div>
      {hand && dealerName && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 rounded-xl bg-cream/[0.06] px-3 py-2.5 ring-1 ring-cream/10">
          <p className="min-w-0 text-sm leading-snug text-cream/90">
            <Name className="font-bold text-cream">{dealerName}</Name> is dealing
            {caller != null && caller !== dealer && (
              <>
                {' '}
                to <Name className="font-bold text-cream">{seats[caller]!.name}</Name>
              </>
            )}
            {callerTeamName && (
              <span className="block text-xs text-cream/60">
                {callerTeamName} need {TRICKS_TO_SAVE} tricks to be saved
              </span>
            )}
          </p>
          <div className="flex shrink-0 items-center gap-2">
            {hand.dealsPerTurn > 1 && (
              <span className="flex items-center gap-1.5 text-xs text-cream/70">
                Hand <span className="font-bold text-cream">{hand.dealInTurn}</span>/{hand.dealsPerTurn}
                {hand.lastOfTurn && <span className="rounded-full bg-gold/25 px-1.5 py-0.5 text-[9px] font-bold text-gold uppercase">Last</span>}
              </span>
            )}
            {onSpeak && (
              <button
                type="button"
                onClick={onSpeak}
                className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm ring-1 ring-cream/20 hover:bg-cream/10"
                aria-label="Say who is dealing, in Hindi"
                title="Announce"
              >
                🔊
              </button>
            )}
          </div>
          {hand.dealsPerTurn > 1 && <div className="w-full">{hand.pips}</div>}
          {hand.nextDealerName && (
            <p className="w-full text-right text-[11px] text-cream/50">
              Next deck → <Name className="font-semibold text-cream/80">{hand.nextDealerName}</Name>
            </p>
          )}
        </div>
      )}

      <div
        ref={containerRef}
        className="relative grid grid-cols-[1fr_minmax(7.5rem,1.3fr)_1fr] grid-rows-[auto_minmax(7rem,auto)_auto] items-center gap-x-2 gap-y-3"
        role="group"
        aria-label="Table seats"
      >
        {dealing && <DealArrow containerRef={containerRef} from={dealer} to={caller} color={callerTeamColor!} />}
        <div
          className="col-start-2 row-start-2 grid h-full min-h-28 place-items-center rounded-[2rem] p-2 text-center shadow-[inset_0_0_25px_rgb(0_0_0/0.55)] ring-4 ring-[#5b3a1e] outline-2 outline-offset-2 outline-gold/50"
          style={{ background: 'radial-gradient(120% 120% at 50% 20%, var(--color-felt-700), var(--color-felt-950))' }}
          aria-hidden={!center}
        >
          {center}
        </div>
        {([0, 1, 2, 3] as Seat[]).map((seat) => {
          const p = seats[seat]!;
          const team = teams[teamOfSeat(seat)];
          const isDealer = dealer === seat;
          const isCaller = caller === seat;
          const label = [
            `${SEAT_NAMES[seat]}: ${p.name}, ${team.name}`,
            isDealer && 'dealer',
            isCaller && 'chooses Rang',
            guaa && `${guaa[seat]} Guaa`,
            dealt && dealsPerPlayer && `dealt ${dealt[seat]} of ${dealsPerPlayer}`,
          ]
            .filter(Boolean)
            .join(', ');
          return (
            <div
              key={seat}
              data-seat={seat}
              className={`${AREA[seat]} relative z-[2] flex min-w-0 flex-col items-center gap-1.5 text-center`}
              aria-label={label}
              role="group"
            >
              <div className="relative">
                <div className={`rounded-full ${isDealer ? 'animate-pulse-ring' : ''}`}>
                  <Avatar name={p.name} avatarKey={p.avatarKey} size={58} ring={isDealer ? 'var(--color-gold)' : team.color} fill={teamAvatarFill(team.color)} />
                </div>
                {(isDealer || isCaller) && (
                  <span
                    className={`absolute -bottom-2.5 left-1/2 -translate-x-1/2 rounded-full px-2 py-0.5 text-[9px] font-extrabold tracking-wider whitespace-nowrap shadow-md ring-2 ring-felt-950 ${
                      isDealer ? 'bg-gold text-ink' : 'bg-cream text-ink'
                    }`}
                    aria-hidden
                  >
                    {isDealer ? 'DEALING' : 'RANG'}
                  </span>
                )}
              </div>
              <Name className="mt-1 block max-w-full truncate px-1 text-sm font-semibold">{p.name}</Name>
              <span className="flex items-center gap-1.5" aria-hidden>
                <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: team.color }} />
                {guaa && (
                  <span
                    className={`rounded-full px-1.5 text-[11px] font-bold tabular-nums ${
                      guaa[seat] > 0 ? 'bg-suit-red text-white' : 'bg-success-700/25 text-success-500'
                    }`}
                  >
                    {guaa[seat]} Guaa
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
